/**
 * YearGlass Sanctuary — Procedural Web Audio Engine
 *
 * Fully procedural ambient soundscape tuned to recommended specifications:
 *   - Master Volume Cap: 0.8
 *   - Ambient Gain: 0.20
 *   - Radio Gain: 0.15
 *   - Softer water drop & shimmer effects with no clipping
 *   - Automatic Audio Suspension on tab/app hide (visibilitychange)
 */

export type YearglassSound = 'rain' | 'bird' | 'hum' | 'shimmer';

interface ActiveNode {
  stop: () => void;
  disconnect: () => void;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private radioGain: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private started = false;
  private disposed = false;
  private birdTimer = 0;
  private readonly active: ActiveNode[] = [];
  private readonly unlockHandlers: Array<() => void> = [];

  constructor() {
    this.installVisibilityListener();
  }

  installVisibilityListener(): () => void {
    if (typeof document === 'undefined') return () => undefined;
    const handler = () => {
      if (this.disposed || !this.ctx) return;
      if (document.hidden || document.visibilityState === 'hidden') {
        if (this.ctx.state === 'running') {
          void this.ctx.suspend();
        }
      } else if (document.visibilityState === 'visible') {
        if (this.ctx.state === 'suspended' && this.started) {
          void this.ctx.resume();
        }
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }

  async unlock(): Promise<boolean> {
    if (this.disposed) return false;
    if (this.ctx && this.ctx.state === 'running') return true;
    try {
      const AC =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) return false;
      if (!this.ctx) {
        this.ctx = new AC();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.80; // Master volume cap: 0.8
        this.master.connect(this.ctx.destination);
        this.buildNoiseBuffer();
      }
      if (this.ctx.state === 'suspended' && (!document.hidden)) {
        await this.ctx.resume();
      }
      return true;
    } catch {
      return false;
    }
  }

  installGestureUnlock(): () => void {
    if (typeof window === 'undefined') return () => undefined;
    const targets = ['pointerdown', 'touchstart', 'keydown'] as const;
    for (const type of targets) {
      const handler = () => {
        if (this.disposed) return;
        void this.unlock().then((ok) => {
          if (ok) {
            this.startAmbient();
            this.removeGestureUnlock();
          }
        });
      };
      window.addEventListener(type, handler, { once: true, passive: true });
      this.unlockHandlers.push(() => window.removeEventListener(type, handler));
    }
    return () => this.removeGestureUnlock();
  }

  removeGestureUnlock(): void {
    for (const remove of this.unlockHandlers.splice(0)) {
      try {
        remove();
      } catch {
        /* ignore */
      }
    }
  }

  startAmbient(): void {
    if (this.started || !this.ctx || !this.master) return;
    this.started = true;

    // Ambient gain: 0.20
    this.ambientGain = this.ctx.createGain();
    this.ambientGain.gain.value = 0;
    this.ambientGain.connect(this.master);

    // Radio gain: 0.15
    this.radioGain = this.ctx.createGain();
    this.radioGain.gain.value = 0.15;
    this.radioGain.connect(this.master);

    this.startRain(this.ambientGain);
    this.startHum(this.ambientGain);
    this.ambientGain.gain.linearRampToValueAtTime(0.20, this.ctx.currentTime + 2.5);
    this.scheduleBirds();
  }

  stopAmbient(): void {
    if (!this.ctx) return;
    this.started = false;
    this.clearBirdTimer();
    const gain = this.ambientGain;
    if (gain) {
      gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.8);
      window.setTimeout(() => {
        for (const node of this.active.splice(0)) {
          try {
            node.stop();
            node.disconnect();
          } catch {
            /* ignore */
          }
        }
      }, 900);
    }
    this.ambientGain = null;
    this.radioGain = null;
  }

  private clearBirdTimer(): void {
    if (this.birdTimer) {
      window.clearTimeout(this.birdTimer);
      this.birdTimer = 0;
    }
  }

  private buildNoiseBuffer(): void {
    if (!this.ctx) return;
    const len = Math.floor(this.ctx.sampleRate * 2);
    const buffer = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 2.8;
    }
    this.noiseBuffer = buffer;
  }

  private startRain(dest: AudioNode): void {
    const ctx = this.ctx as AudioContext;
    if (!this.noiseBuffer) return;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.loop = true;

    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 850;
    band.Q.value = 0.5;

    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.12;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 220;
    lfo.connect(lfoGain);
    lfoGain.connect(band.frequency);

    const wet = ctx.createGain();
    wet.gain.value = 0.25;

    source.connect(band);
    band.connect(wet);
    wet.connect(dest);
    source.start();
    lfo.start();

    this.active.push({
      stop: () => {
        try {
          source.stop();
          lfo.stop();
        } catch {
          /* ignore */
        }
      },
      disconnect: () => {
        source.disconnect();
        lfo.disconnect();
      },
    });
  }

  private startHum(dest: AudioNode): void {
    const ctx = this.ctx as AudioContext;
    const o1 = ctx.createOscillator();
    o1.type = 'sine';
    o1.frequency.value = 55;
    const o2 = ctx.createOscillator();
    o2.type = 'sine';
    o2.frequency.value = 82.5;
    const g = ctx.createGain();
    g.gain.value = 0.08;
    o1.connect(g);
    o2.connect(g);
    g.connect(dest);
    o1.start();
    o2.start();

    this.active.push({
      stop: () => {
        try {
          o1.stop();
          o2.stop();
        } catch {
          /* ignore */
        }
      },
      disconnect: () => {
        o1.disconnect();
        o2.disconnect();
        g.disconnect();
      },
    });
  }

  private scheduleBirds(): void {
    this.clearBirdTimer();
    const chirp = () => {
      if (this.disposed || !this.ctx || !this.master) return;
      this.playChirp(0.20 + Math.random() * 0.18);
    };
    const loop = () => {
      if (this.disposed || !this.ctx || !this.started) return;
      const delay = 5000 + Math.random() * 8000;
      this.birdTimer = window.setTimeout(() => {
        if (this.disposed) return;
        this.birdTimer = 0;
        chirp();
        loop();
      }, delay);
    };
    loop();
  }

  private playChirp(vol: number): void {
    const ctx = this.ctx as AudioContext;
    const t0 = ctx.currentTime;
    const dur = 0.10 + Math.random() * 0.06;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    const carrier = 2000 + Math.random() * 1400;
    osc.frequency.setValueAtTime(carrier, t0);
    osc.frequency.exponentialRampToValueAtTime(carrier * (0.88 + Math.random() * 0.25), t0 + dur);

    const mod = ctx.createOscillator();
    mod.frequency.value = 35 + Math.random() * 50;
    const modGain = ctx.createGain();
    modGain.gain.value = carrier * 0.4;
    mod.connect(modGain);
    modGain.connect(osc.frequency);

    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(vol, t0 + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    osc.connect(g);
    g.connect(this.master as GainNode);
    osc.start(t0);
    mod.start(t0);
    osc.stop(t0 + dur + 0.02);
    mod.stop(t0 + dur + 0.02);
  }

  playShimmer(): void {
    const ctx = this.ctx as AudioContext;
    if (!ctx || ctx.state !== 'running') return;
    const base = 523.25;
    const steps = [0, 4, 7, 12, 16];
    steps.forEach((st, i) => {
      const t0 = ctx.currentTime + i * 0.10;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = base * Math.pow(2, st / 12);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.12, t0 + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.7);
      osc.connect(g);
      g.connect(this.master as GainNode);
      osc.start(t0);
      osc.stop(t0 + 0.8);
    });
  }

  playWaterDrop(): void {
    const ctx = this.ctx as AudioContext;
    if (!ctx || ctx.state !== 'running') return;
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(750, t0);
    osc.frequency.exponentialRampToValueAtTime(310, t0 + 0.10);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.12, t0 + 0.012); // Softer water effects with no clipping
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.12);
    osc.connect(g);
    g.connect(this.master as GainNode);
    osc.start(t0);
    osc.stop(t0 + 0.14);
  }

  playButtonTap(): void {
    const ctx = this.ctx as AudioContext;
    if (!ctx || ctx.state !== 'running') return;
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(450, t0);
    osc.frequency.exponentialRampToValueAtTime(220, t0 + 0.035);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.10, t0 + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.04);
    osc.connect(g);
    g.connect(this.master as GainNode);
    osc.start(t0);
    osc.stop(t0 + 0.045);
  }

  play(sound: YearglassSound): void {
    if (!this.ctx) return;
    if (sound === 'shimmer') {
      this.playShimmer();
    } else if (sound === 'hum') {
      this.playWaterDrop();
    }
  }

  destroy(): void {
    this.disposed = true;
    this.clearBirdTimer();
    this.removeGestureUnlock();
    if (this.ctx && this.ctx.state !== 'closed') {
      void this.ctx.close();
    }
    for (const node of this.active.splice(0)) {
      try {
        node.stop();
        node.disconnect();
      } catch {
        /* ignore */
      }
    }
    this.ctx = null;
    this.master = null;
    this.ambientGain = null;
    this.radioGain = null;
    this.started = false;
  }
}
